import { FileText, Folder, LinkIcon, PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { safeUrl } from "@/lib/sanitize";
import { listMediaBySlug } from "@/features/knowledge/api";
import { buildTree, type MediaTreeNode } from "@/features/knowledge/lib/tree";

function LeafIcon({ type }: { type: MediaTreeNode["assetType"] }) {
  if (type === "VIDEO") return <PlayCircle className="text-muted-foreground size-4 shrink-0" />;
  if (type === "PDF") return <FileText className="text-muted-foreground size-4 shrink-0" />;
  if (type === "LINK") return <LinkIcon className="text-muted-foreground size-4 shrink-0" />;
  return <Folder className="text-muted-foreground size-4 shrink-0" />;
}

function TreeNodes({ nodes }: { nodes: MediaTreeNode[] }) {
  return (
    <ul className="space-y-1">
      {nodes.map((node) => {
        if (node.assetType === "DIR") {
          return (
            <li key={node.id}>
              <p className="mt-3 mb-1 flex items-center gap-2 font-semibold">
                <Folder className="text-primary size-4" />
                {node.description}
              </p>
              <div className="border-border ml-4 border-l pl-3">
                <TreeNodes nodes={node.children} />
              </div>
            </li>
          );
        }
        if (node.assetType === "TEXT") {
          return (
            <li key={node.id} className="py-1">
              <SanitizedHtml html={node.detail} />
            </li>
          );
        }
        return (
          <li key={node.id}>
            <a
              href={safeUrl(node.href)}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:bg-accent flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
            >
              <LeafIcon type={node.assetType} />
              {node.description}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Nested media-library category tree (Software / Resources). The category slug is
 * the last path segment (`/kb/library/software` → `software`).
 */
export default function LibraryCategoryPage() {
  const slug = useLocation().pathname.split("/").filter(Boolean).pop() ?? "";
  const [tree, setTree] = useState<MediaTreeNode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listMediaBySlug(slug)
      .then((items) => !cancelled && setTree(buildTree(items)))
      .catch(() => !cancelled && toast.error("Could not load this category."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-6 w-1/2" />
      </div>
    );
  }

  if (tree.length === 0) {
    return <p className="text-muted-foreground text-sm">Nothing in this category yet.</p>;
  }

  return (
    <Card className="p-5">
      <TreeNodes nodes={tree} />
    </Card>
  );
}
